import { Repeat2, Layers, Tag } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

// ── Viral Video Remake ────────────────────────────────────────────────────────
//
// A second job hierarchy on cr_campaigns (is_viral_remake boolean, see
// supabase/migrations/20260902000000_cr_campaigns_viral_remake.sql). On a remake
// campaign the brief flips meaning: brief_video_urls is no longer style
// inspiration but a worklist — each entry is a specific viral video the creator
// is hired to remake, with a per-entry description of what to keep and swap.
//
// Admin is READ-ONLY for these fields: nothing here writes, so nothing here is
// audit-logged.
//
// Always the FULL label, everywhere — an earlier "Remake"-only short form in
// tight table cells was dropped 2026-09-02 per product feedback: the badge is
// the only signal a scanning admin gets, so it has to name the hierarchy on
// its own. Widen the containing cell/column rather than re-abbreviating it.
//
// Strings and the "electric blue" (sky→blue gradient) accent are shared
// verbatim with the brand dashboard and the creator app — do not reword or
// recolor on one surface only. An amber version shipped first but was
// dropped the same day per product feedback: amber already means "needs
// attention" all over this admin (waitlisted/pending/warning badges), so a
// remake campaign kept reading as a problem instead of a job type. Blue
// avoids every other color already spoken for here — emerald is Hybrid,
// purple is BMC Max, plain blue-600 is Reward Only (a lighter sky/blue
// gradient reads as distinct from that flat fill), amber/orange is warning.

export const VIRAL_REMAKE_LABEL = 'Viral Video Remake';
/** Brief-videos section heading on a remake campaign. */
export const VIDEOS_TO_REMAKE_HEADING = 'Videos To Remake';
/** Label above a single entry's remake instructions. */
export const WHAT_TO_REMAKE_LABEL = 'What to remake';

/**
 * A brief_video_urls entry. `name` and `description` are both optional on every
 * row: standard campaigns and every row created before this hierarchy existed
 * simply have no description key.
 */
export type BriefVideoEntry = {
  url: string;
  name?: string | null;
  description?: string | null;
};

/** Missing / null / non-boolean is_viral_remake reads as a standard campaign. */
export function isViralRemake(campaign: { is_viral_remake?: boolean | null } | null | undefined): boolean {
  return campaign?.is_viral_remake === true;
}

/**
 * Normalises brief_video_urls into renderable entries. Null, a non-array, or a
 * jsonb array holding malformed members (no string url) all degrade to an empty
 * list / skipped members rather than throwing or rendering a broken link.
 */
export function briefVideoEntries(value: unknown): BriefVideoEntry[] {
  if (!Array.isArray(value)) return [];
  return (value as unknown[]).flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const entry = item as Record<string, unknown>;
    if (typeof entry.url !== 'string' || !entry.url.trim()) return [];
    return [{
      url: entry.url,
      name: typeof entry.name === 'string' ? entry.name : null,
      description: typeof entry.description === 'string' ? entry.description : null,
    }];
  });
}

/** Whitespace-only descriptions must not render the label. */
export function remakeDescription(entry: { description?: string | null } | null | undefined): string | null {
  const d = entry?.description;
  return typeof d === 'string' && d.trim() ? d : null;
}

/**
 * The one Viral Video Remake pill. Solid sky→blue gradient with a small
 * icon, not the light bg-*-100/text-*-800 tint every status badge on these
 * screens uses — deliberately, so it reads as a distinct job hierarchy at a
 * glance rather than another status. Softly glowing shadow gives it the
 * "premium" weight a flat tint doesn't have.
 */
export function ViralRemakeBadge({ className = '' }: { className?: string }) {
  return (
    <Badge
      className={`gap-1 border-0 bg-gradient-to-r from-sky-400 to-blue-500 text-white shadow-sm shadow-blue-500/40 ${className}`.trim()}
    >
      <Repeat2 className="h-3 w-3" strokeWidth={2.5} />
      {VIRAL_REMAKE_LABEL}
    </Badge>
  );
}

// ── Multi-Product campaigns ─────────────────────────────────────────────────
//
// cr_campaign_products (see supabase/migrations/20260910000002_cr_campaign_products.sql)
// lets a campaign promote more than one TikTok Shop product. A campaign can
// be multi-product AND a remake at once, so the two badges need to read as
// visually distinct attributes, not shades of the same thing — a first cyan
// version shipped, then was dropped the same day per product feedback: cyan
// and the sky→blue Viral Video Remake gradient read as near-identical at
// badge size. Indigo is clearly more violet/purple than either blue. Strings
// and the gradient accent are shared verbatim with the brand dashboard and
// the creator app — do not reword or recolor on one surface only. Indigo
// avoids every other color already spoken for here too — emerald is Hybrid,
// purple is BMC Max, plain blue-600 is Reward Only, amber/orange is warning.

export const MULTI_PRODUCT_LABEL = 'Product-Bundle';

/** True when a campaign's embedded cr_campaign_products carries 2+ rows. */
export function isMultiProduct(campaign: { cr_campaign_products?: { id: string }[] | null } | null | undefined): boolean {
  return (campaign?.cr_campaign_products?.length ?? 0) > 1;
}

/**
 * The one Multi-Product pill — same solid-gradient treatment as
 * ViralRemakeBadge (not the light bg-*-100/text-*-800 tint every status
 * badge on these screens uses), so it reads as a campaign attribute, not
 * another status. `count`, when passed, swaps the label to "N Products" —
 * used where there's room and the exact count is useful (the detail header).
 */
export function MultiProductBadge({ className = '', count }: { className?: string; count?: number }) {
  return (
    <Badge
      className={`gap-1 border-0 bg-gradient-to-r from-indigo-400 to-indigo-600 text-white shadow-sm shadow-indigo-600/40 ${className}`.trim()}
    >
      <Layers className="h-3 w-3" strokeWidth={2.5} />
      {count ? `${count} Products` : MULTI_PRODUCT_LABEL}
    </Badge>
  );
}

// ── TikTok Shop collaboration type ──────────────────────────────────────────
//
// Optional, brand-reported (2026-09-25, cr_campaigns.tiktok_collab_type — see
// the migration for the full definitions). Admin-visible only: this is a
// classification the brand entered in the campaign wizard, not a feature this
// platform runs — nothing reads it to change matching, payouts or what a
// creator sees, so there is no toggle or write path here, only display.
//
// Deliberately the light bg-*-100/text-*-800 tint every status badge on these
// screens uses, NOT the solid gradient ViralRemakeBadge/MultiProductBadge use
// — those two mark a distinct JOB HIERARCHY this platform actually runs
// differently; this is closer to a status tag (which of three TikTok Shop
// programs the brand said applies), so it should read at that lighter weight.
// Colors avoid every gradient/tint already meaningful on these screens
// (status badges, HireStatusBadge, the two pills above).

const TIKTOK_COLLAB_TYPE_META: Record<string, { label: string; className: string }> = {
  open_collaboration: { label: 'Open Collaboration', className: 'bg-teal-100 text-teal-800 border-teal-200' },
  target_collaboration: { label: 'Target Collaboration', className: 'bg-violet-100 text-violet-800 border-violet-200' },
  tap: { label: 'TAP', className: 'bg-cyan-100 text-cyan-800 border-cyan-200' },
};

/** Renders nothing for null/unset/unrecognized — "not specified" is a normal
 *  resting state for this field, not something to badge as missing. */
export function TiktokCollabTypeBadge({ value, className = '' }: { value?: string | null; className?: string }) {
  const meta = value ? TIKTOK_COLLAB_TYPE_META[value] : undefined;
  if (!meta) return null;
  return (
    <Badge className={`gap-1 border ${meta.className} ${className}`.trim()}>
      <Tag className="h-3 w-3" />
      {meta.label}
    </Badge>
  );
}
